import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { KPI } from '@app/insights/insights-api.model';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TranslateFn, translateWithDefaults } from '@app/shared/utils';
import { shortenBigNumber } from '@app/shared/utils/common-utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'dgx-kpi-item',
    templateUrl: './kpi-item.component.html',
    styleUrls: ['./kpi-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KpiItemComponent {
  @Input() public kpi: KPI;
  @Input() public kpiIndex: number;

  private translate: TranslateFn;

  public get showKPITrend() {
    return this.kpi.trend !== undefined && !isNaN(this.kpi.trend);
  }

  public get trendOverTime() {
    if (!this.showKPITrend) {
      return;
    }

    const percent = shortenBigNumber(Math.abs(this.kpi.trend || 0), 1);

    if (this.kpi.numOfDays === 365) {
      return this.translate('[n]% in the last year', 'KPI_OverTheLastYear', {
        percent,
      });
    }

    if (this.kpi.numOfDays > 365) {
      return this.translate(
        '[n]% in the last [n] years',
        'KPI_OverTheLastXYears',
        {
          number: (this.kpi.numOfDays / 365).toFixed(1),
          percent,
        }
      );
    }

    return this.translate('[n]% in the last [n] days', 'KPI_OverTheLastXDays', {
      number: this.kpi.numOfDays,
      percent,
    });
  }

  public get icon() {
    if (this.kpi.trend > 0) {
      return {
        type: 'triangle-up',
        class: 'color-green',
        label: this.translate('Increase', 'KPI_Increase'),
      };
    }

    if (this.kpi.trend < 0) {
      return {
        type: 'triangle-down',
        class: 'color-red',
        label: this.translate('Decrease', 'KPI_Decrease'),
      };
    }

    if (this.kpi.trend * 1 === 0) {
      return {
        type: 'triangle-up',
        class: 'color-ebony-a61 rotate-icon-90',
        label: this.translate('No increase or decrease', 'KPI_NoChange'),
      };
    }
  }

  constructor(
    private translateService: TranslateService,
    private tracker: TrackerService
  ) {
    this.translate = translateWithDefaults(this.translateService);
  }

  public trackHover(kpi: KPI) {
    this.tracker.trackEventData({
      action: 'Insights KPI Hovered',
      properties: { Name: kpi.name, Location: kpi.location },
    });
  }
}
